import { useTextContentElementToAdd } from 'components/WorkspaceEditor/Workspace2Store'
import { MutableRefObject, useCallback, useEffect, useRef } from 'react'
import { useCKEditorToggle } from './useCKEditorToggle'

// Create a global focus tracker to prevent simultaneous focusing
// This is a singleton object that exists outside of the React component lifecycle
const FocusTracker = {
    activeEditorId: null as string | null,
    registerEditor(
        editorId: string | undefined,
        callback?: () => void
    ): boolean {
        // If no ID, don't change anything
        if (!editorId) {
            return false
        }

        // Set this editor as active
        this.activeEditorId = editorId
        callback?.()
        return true
    },
    unregisterEditor(editorId: string | undefined): void {
        if (editorId && this.activeEditorId === editorId) {
            this.activeEditorId = null
        }
    },
}

/** Props for the useContentEditorFocus hook. */
interface UseContentEditorFocusProps {
    /** The ID of the content item to be focused. */
    contentItemId?: string
    /** The optional handler to activate CKEditor. */
    activate?: () => void
    /** Optional control type to distinguish between different editor types */
    controlType?: 'plainText' | 'replacementOption'
}

/**
 * Custom hook that handles setting focus on `editingTextContentElement` by matching its id.
 * Uses a global focus tracker to prevent simultaneous focusing of multiple editors.
 *
 * @param {UseContentEditorFocusProps} props - Props for the hook.
 * @param {string} props.contentItemId - The ID of the content item to be focused.
 * @param {object} props.activate - The optional handler to activate CKEditor.
 * @param {string} props.controlType - Optional type to distinguish different editors.
 * @returns The CKEditor (or type of T) ref to focus with.
 */
export const useContentEditorFocus = <T extends { focus: () => void }>({
    contentItemId,
    activate,
    controlType = 'plainText',
}: UseContentEditorFocusProps) => {
    const editorFocusRef: MutableRefObject<T | null> = useRef(null)
    const ckeToggle = useCKEditorToggle(contentItemId)
    const textContentElementToAdd = useTextContentElementToAdd()

    // Create a unique ID combining contentItemId and controlType
    const uniqueEditorId = contentItemId
        ? `${contentItemId}:${controlType}`
        : undefined

    // Manually focus the editor with proper prioritization
    const focusEditor = useCallback(() => {
        if (uniqueEditorId) {
            FocusTracker.registerEditor(uniqueEditorId, () => {
                const shouldProcessContentItem =
                    contentItemId &&
                    (!textContentElementToAdd ||
                        textContentElementToAdd.id === contentItemId)
                if (shouldProcessContentItem) {
                    activate?.()

                    // Use double requestAnimationFrame for reliable DOM measurement
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            if (
                                editorFocusRef.current &&
                                FocusTracker.activeEditorId === uniqueEditorId
                            ) {
                                editorFocusRef.current.focus()
                            }
                        })
                    })
                }
            })
        }
    }, [
        uniqueEditorId,
        contentItemId,
        textContentElementToAdd,
        activate,
        editorFocusRef,
    ])

    // Defocus the editor when there's an add operation
    const unFocusEditor = useCallback(() => {
        if (editorFocusRef?.current) {
            editorFocusRef.current = null
            ckeToggle.deactivate()

            // Unregister this editor from the focus tracker
            if (uniqueEditorId) {
                FocusTracker.unregisterEditor(uniqueEditorId)
            }
        }
    }, [editorFocusRef, ckeToggle, uniqueEditorId])

    // Clean up when the component is unmounted
    useEffect(() => {
        return () => {
            if (uniqueEditorId) {
                FocusTracker.unregisterEditor(uniqueEditorId)
            }
        }
    }, [uniqueEditorId])

    return { editorFocusRef, unFocusEditor, focusEditor }
}
